import { create } from 'zustand';

type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

interface ConnectionState {
  status: ConnectionStatus;
  lastConnectedAt: Date | null;
  reconnectAttempts: number;

  setStatus: (status: ConnectionStatus) => void;
  handleDisconnect: () => void;
  handleReconnect: () => void;
  initOnlineListener: () => () => void;
}

// Callbacks registered by stores for resubscription on reconnect
const resubscribeCallbacks: Set<() => void> = new Set();

export function registerResubscribe(cb: () => void): () => void {
  resubscribeCallbacks.add(cb);
  return () => resubscribeCallbacks.delete(cb);
}

export const useConnectionStore = create<ConnectionState>((set, get) => ({
  status: 'connected',
  lastConnectedAt: null,
  reconnectAttempts: 0,

  setStatus: (status) => set({ status }),

  handleDisconnect: () => {
    set((s) => ({
      status: 'disconnected',
      reconnectAttempts: s.reconnectAttempts + 1,
    }));
  },

  handleReconnect: () => {
    set({
      status: 'connected',
      lastConnectedAt: new Date(),
      reconnectAttempts: 0,
    });
    // Trigger resubscriptions
    for (const cb of resubscribeCallbacks) {
      try { cb(); } catch (e) { console.warn('[connectionStore] resubscribe error:', e); }
    }
  },

  initOnlineListener: () => {
    const onOnline = () => {
      console.log('[connectionStore] Browser back online, reconnecting...');
      set({ status: 'reconnecting' });
      // Small delay to let Supabase reconnect, then trigger resubscriptions
      setTimeout(() => get().handleReconnect(), 1500);
    };
    const onOffline = () => {
      console.log('[connectionStore] Browser went offline');
      get().handleDisconnect();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  },
}));
